import { createSlice } from '@reduxjs/toolkit';
import { PURGE } from 'redux-persist';

/**
 * OTRIX Play — motor de ejecución de "Iniciar rutina".
 *
 * El cronómetro vive aquí (Redux) y se persiste con redux-persist, así el entrenamiento
 * sobrevive al navegar entre pantallas Y al recargar la página. La FUENTE DE VERDAD del
 * tiempo son timestamps (`Date.now()`): la UI recalcula los segundos desde ellos en cada
 * render, en vez de un contador que se detendría al desmontar el componente. Por eso no
 * guardamos un "tick" por segundo (evitamos escrituras constantes en localforage).
 */

const initialState = {
  active: false,
  session: null
};

/**
 * Por debajo de qué fracción del descanso prescrito se considera que la persona SE LO SALTÓ.
 * 0.6 deja pasar el corte normal de quien ya está listo (cortar a los 80 s de 90 no es saltárselo)
 * y solo marca el recorte que sí cambia el estímulo. Lo lee restAdvice.js para el aviso ACSM.
 */
export const REST_SKIP_RATIO = 0.6;

// Segundos consumidos en la fase actual (para la cuenta regresiva del descanso).
export const currentPhaseSeconds = (session, now) => {
  if (!session) return 0;
  const running =
    session.phase !== 'paused' && session.phaseStartedAt
      ? Math.max(0, Math.floor((now - session.phaseStartedAt) / 1000))
      : 0;
  return session.phaseBaseSeconds + running;
};

// Finaliza los segundos en curso de la fase actual, sumándolos a los acumuladores.
// Devuelve el objeto session mutado (immer) — usar SOLO dentro de reducers.
const finalizeRunning = (session, now) => {
  if (!session || session.phase === 'paused' || !session.phaseStartedAt) return 0;
  const run = Math.max(0, Math.floor((now - session.phaseStartedAt) / 1000));
  if (session.phase === 'rest') {
    session.restSeconds += run;
  } else {
    session.workSeconds += run;
  }
  session.phaseBaseSeconds += run;
  return run;
};

const playWorkoutSlice = createSlice({
  name: 'playWorkout',
  initialState,
  reducers: {
    // Prepara el entrenamiento. `exercises` viene ya armado (buildWorkoutPlan) desde la card.
    // Queda en 'ready': el cronómetro NO corre hasta que el usuario da Play (acción `begin`).
    // Antes arrancaba solo al navegar y el tiempo empezaba a correr sin que la persona estuviera lista.
    start(state, action) {
      const { dayOfWeek, muscleFocus, exercises, now, progressionWeek } = action.payload;
      state.active = true;
      state.session = {
        dayOfWeek: dayOfWeek ?? null,
        muscleFocus: muscleFocus ?? null,
        // Semana del mesociclo (1..4) con la que se armó el plan; null = rutina sin ciclo.
        progressionWeek: progressionWeek ?? null,
        startedAt: now,
        exercises,
        currentExerciseIndex: 0,
        currentSet: 1,
        phase: 'ready', // 'ready' | 'work' | 'rest' | 'paused' | 'done'
        pausedPhase: null,
        phaseStartedAt: null,
        phaseBaseSeconds: 0,
        restDuration: 0,
        workSeconds: 0,
        restSeconds: 0
      };
    },

    // Play inicial: pasa de 'ready' a 'work' y arranca el reloj.
    begin(state, action) {
      const s = state.session;
      if (!s || s.phase !== 'ready') return;
      const { now } = action.payload || {};
      s.phase = 'work';
      s.startedAt = now;
      s.phaseStartedAt = now;
      s.phaseBaseSeconds = 0;
    },

    // Termina la serie actual: registra lo que REALMENTE se hizo (reps, peso y segundos
    // cronometrados de esa serie) y decide si toca descanso o fin.
    completeSet(state, action) {
      const s = state.session;
      if (!s || s.phase === 'done' || s.phase === 'ready') return;
      const { reps, weight, now } = action.payload || {};
      // `finalizeRunning` YA calculaba los segundos de la serie y se descartaban: son el dato
      // de las métricas de tiempo (plancha, cardio…), así que ahora se guardan por serie.
      const seconds = finalizeRunning(s, now);

      const ex = s.exercises[s.currentExerciseIndex];
      if (ex) {
        const index = s.currentSet - 1;
        if (weight !== undefined && weight !== null && weight !== '') {
          ex.weights[index] = Number(weight);
        }
        // Reps REALES. Sin esto el backend registraba las OBJETIVO ("8-12" → 12), así que
        // quien hacía 5 quedaba con 12.
        if (reps !== undefined && reps !== null && reps !== '') {
          ex.repsDone[index] = Number(reps);
        }
        ex.secondsDone[index] = seconds;
        ex.setsDone = s.currentSet;
      }

      const isLastSet = s.currentSet >= (ex?.sets || 1);
      const isLastExercise = s.currentExerciseIndex >= s.exercises.length - 1;

      if (isLastSet && isLastExercise) {
        s.phase = 'done';
        s.phaseStartedAt = null;
        s.phaseBaseSeconds = 0;
        s.restDuration = 0;
        return;
      }

      // Descanso previo a la siguiente serie / siguiente ejercicio.
      s.phase = 'rest';
      s.restDuration = isLastSet ? ex.restBetweenExercises : ex.restBetweenSets;
      s.phaseStartedAt = now;
      s.phaseBaseSeconds = 0;
    },

    // Termina el descanso (natural o "saltar") y avanza a la siguiente serie/ejercicio.
    // `skipped` lo manda SOLO el botón "Saltar descanso"; el piloto automático (el descanso que
    // llega a cero solo) despacha esta misma acción sin la marca.
    endRest(state, action) {
      const s = state.session;
      if (!s || s.phase !== 'rest') return;
      const { now, skipped } = action.payload || {};
      // Hay que leer los segundos de la fase ANTES de cerrarla: `finalizeRunning` los vuelca a los
      // acumuladores y deja de haber con qué comparar el descanso prescrito.
      const done = currentPhaseSeconds(s, now);
      finalizeRunning(s, now);

      const ex = s.exercises[s.currentExerciseIndex];
      if (ex) {
        const index = s.currentSet - 1;
        // Las sesiones que quedaron a medias en redux-persist antes de esta versión no traen
        // estos arrays: se crean al vuelo para no perder el descanso en curso.
        if (!ex.restDone) ex.restDone = [];
        if (!ex.restSkipped) ex.restSkipped = [];
        ex.restDone[index] = done;
        ex.restSkipped[index] = Boolean(skipped) && done < s.restDuration * REST_SKIP_RATIO;
      }

      const finishedExercise = s.currentSet >= (ex?.sets || 1);
      if (finishedExercise) {
        s.currentExerciseIndex += 1;
        s.currentSet = 1;
      } else {
        s.currentSet += 1;
      }

      s.phase = 'work';
      s.restDuration = 0;
      s.phaseStartedAt = now;
      s.phaseBaseSeconds = 0;
    },

    pause(state, action) {
      const s = state.session;
      if (!s || s.phase === 'paused' || s.phase === 'done' || s.phase === 'ready') return;
      finalizeRunning(s, action.payload?.now);
      s.pausedPhase = s.phase;
      s.phase = 'paused';
      s.phaseStartedAt = null;
    },

    resume(state, action) {
      const s = state.session;
      if (!s || s.phase !== 'paused') return;
      s.phase = s.pausedPhase || 'work';
      s.pausedPhase = null;
      s.phaseStartedAt = action.payload?.now;
    },

    // Termina el entrenamiento y limpia el estado (el guardado en backend lo hace el runner
    // leyendo los totales ANTES de despachar esto).
    reset() {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
  }
});

/* ------------------------------- Selectors --------------------------------- */

export const selectWorkoutActive = (state) => state.playWorkout?.active || false;
export const selectWorkoutSession = (state) => state.playWorkout?.session || null;

/* --------------------------- Time helpers (puros) --------------------------- */

// `currentPhaseSeconds` vive arriba: los reducers también lo usan.

// Segundos restantes de descanso (0 si no estamos descansando).
export const restRemainingSeconds = (session, now) => {
  if (!session || session.phase !== 'rest') return 0;
  return Math.max(0, session.restDuration - currentPhaseSeconds(session, now));
};

// Tiempo total transcurrido del entrenamiento (trabajo + descanso).
export const workoutElapsedSeconds = (session, now) => {
  if (!session) return 0;
  const running =
    session.phase !== 'paused' && session.phaseStartedAt
      ? Math.max(0, Math.floor((now - session.phaseStartedAt) / 1000))
      : 0;
  return session.workSeconds + session.restSeconds + running;
};

// Nº de series completadas en todo el entrenamiento.
export const completedSetsCount = (session) => {
  if (!session) return 0;
  return session.exercises.reduce((acc, ex) => acc + (ex.setsDone || 0), 0);
};

export default playWorkoutSlice;
